import React, { useContext, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import InputContext from '@totem/components/ui_components/reportSettings/InputContext';

type Props = {
  children?: React.ReactNode;
};

const InputQueryInterface = ({ children }: Props) => {
  const { input, handleSort, handlePagination, handleFilter, updateCount } =
    useContext(InputContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const isInitialized = React.useRef(false);
  const lastParams = React.useRef('');

  useEffect(() => {
    const page = parseInt(searchParams.get('page') ?? '1', 10);
    const pageSize = parseInt(searchParams.get('pageSize') ?? '10', 10);
    const sortField = searchParams.get('sortField') ?? '';
    const sortDir = searchParams.get('sortDir') ?? 'ascend';

    handlePagination(page, pageSize);

    if (sortField) {
      handleSort(sortField, sortDir);
    }

    input.filters.forEach((filter) => {
      const urlValues = searchParams.getAll(filter.key);
      if (urlValues.length > 0) {
        handleFilter(filter.key, filter.multiple ? urlValues : urlValues[0]);
      }
    });

    isInitialized.current = true;
  }, []);

  useEffect(() => {
    if (!isInitialized.current) return;

    const params = new URLSearchParams();

    if (input.pagination.page !== undefined) {
      params.set('page', String(input.pagination.page));
    }

    if (input.pagination.pageSize !== undefined) {
      params.set('pageSize', String(input.pagination.pageSize));
    }

    if (input.sort.field) {
      params.set('sortField', input.sort.field);

      params.set('sortDir', input.sort.direction);
    }

    input.filters.forEach((filter) => {
      if (filter.multiple && filter.values && filter.values.length > 0) {
        filter.values.forEach((val) => params.append(filter.key, val.value));
      } else if (!filter.multiple && filter.value) {
        params.set(filter.key, filter.value);
      }
    });

    const newParamsStr = params.toString();
    if (newParamsStr === lastParams.current) return;

    lastParams.current = newParamsStr;

    setSearchParams(params, { replace: true });
  }, [
    input.pagination.page,
    input.pagination.pageSize,
    input.sort.field,
    input.sort.direction,
    updateCount,
  ]);

  return <>{children}</>;
};

export default InputQueryInterface;
